// Moving the store between games: entering a state (new, stepped, loaded, restored), writing
// the autosave ring at the start of phase 2 (Section 4) and keeping the saves index signal
// current. Shared by the actions and the error boundary; not an action itself.
import { emptyDecision, type GameState } from '../sim/api';
import {
  latestAutosave,
  listAutosaves,
  listSlots,
  loadEnvelope,
  makeEnvelope,
  writeAutosave,
  type LoadedRun,
} from '../persist';
import { currentRun, newRunId, runConfig, setRun } from './run';
import { draft, game, patchUi, saves, settings, status } from './state';
import { currentStorage } from './storage';

export function refreshSaves(): void {
  const storage = currentStorage();
  const next =
    storage === null
      ? { autosaves: [], slots: [] }
      : { autosaves: listAutosaves(storage), slots: listSlots(storage) };
  saves.value = next;
  patchUi({ hasContinue: next.autosaves.length > 0 });
}

/**
 * Write the autosave for a state at the start of its phase 2 (Section 4). Deduplicated by run
 * and turn; a quota error shrinks the ring to 2 and tells the player (24.12).
 */
export function autosave(state: GameState, kind: 'autosave' | 'start' = 'autosave'): void {
  const storage = currentStorage();
  if (storage === null) return;
  const run = currentRun();
  if (kind === 'autosave') {
    const newest = saves.value.autosaves.find((m) => m.kind === 'autosave');
    if (newest !== undefined && newest.runId === run.runId && newest.turn === state.turn) return;
  }
  const cfg = runConfig();
  const envelope = makeEnvelope(state, new Date().toISOString(), cfg === undefined ? {} : { cfg });
  const result = writeAutosave(storage, envelope, {
    runId: run.runId,
    ringSize: settings.value.autosaveCount,
    kind,
  });
  if (!result.ok) patchUi({ notice: { code: 'quota', detail: null } });
  else if (result.shrunkTo !== null) {
    settings.value = { ...settings.value, autosaveCount: result.shrunkTo };
    patchUi({ notice: { code: 'quota', detail: String(result.shrunkTo) } });
  }
  refreshSaves();
}

/** Status for a state that is between seasons (24.7). */
export function restingStatus(state: GameState): 'deciding' | 'ended' | 'paused' {
  if (state.phaseCursor !== null) return 'paused';
  return state.outcome === null || state.sandbox ? 'deciding' : 'ended';
}

/** Put a state in the store at the start of phase 2 with a fresh draft. */
export function enterDeciding(state: GameState): void {
  game.value = state;
  draft.value = emptyDecision(state);
  status.value = restingStatus(state);
  if (status.value === 'deciding') autosave(state);
}

/**
 * Enter a loaded or continued run: show the last report if there is one (phase 1), otherwise
 * go straight to deciding.
 */
export function enterLoaded(loaded: LoadedRun, runId: string | null): void {
  const differs = loaded.balance.configDiffers || loaded.balance.contentDiffers;
  setRun({
    runId: runId ?? newRunId(loaded.state.seed),
    cfg: loaded.balance.configDiffers ? loaded.cfg : undefined,
    envelope: loaded.envelope,
  });
  const state = loaded.state;
  game.value = state;
  draft.value = emptyDecision(state);
  status.value = restingStatus(state);
  const showReport = state.history.length > 0 && status.value !== 'paused';
  if (status.value === 'deciding' && showReport) status.value = 'reporting';
  patchUi({
    overlay: showReport ? 'report' : null,
    olderBalance: differs ? loaded.balance : null,
    selectedPersonId: null,
    autoFill: null,
    confirmingEnd: false,
  });
}

/** Restore the newest autosave (error recovery, 24.15). Returns false if there is none. */
export function restoreLatest(): boolean {
  const storage = currentStorage();
  const found = storage === null ? null : latestAutosave(storage);
  if (found === null) return false;
  try {
    enterLoaded(loadEnvelope(found.envelope), found.meta.runId);
  } catch {
    return false;
  }
  if (status.value === 'reporting') status.value = 'deciding';
  patchUi({ overlay: null });
  return true;
}
